// src/lib/themeTokens.ts
// Calenddy Clean White Identity Token System
// Rule: Public booking pages are ALWAYS white (#FFFFFF). Brand colors are applied strictly as visual accents.

export interface ThemeTokens {
  isDark: false;
  bgPage: string;
  bgSurface: string;
  bgSubtle: string;
  bgOverlay: string;
  borderSubtle: string;
  borderHover: string;
  textPrimary: string;
  textSecondary: string;
  textMuted: string;
  brandColor: string;
  brandButtonBg: string;
  brandButtonText: string;
  brandBadgeBg: string;
  brandBadgeText: string;
  pillActiveBg: string;
  pillActiveText: string;
  pillInactiveBg: string;
  pillInactiveText: string;
  pillInactiveBorder: string;
  searchBg: string;
  searchBorder: string;
  searchText: string;
  searchPlaceholder: string;
  cardBg: string;
  cardBorder: string;
  cardHoverBorder: string;
  accentLink: string;
  iconBg: string;
  iconText: string;
}

/**
 * Calculates perceived luminance of a hex color (0-255).
 * Standard ITU-R BT.601 formula: Y = 0.299*R + 0.587*G + 0.114*B
 */
export function getLuminance(hexColor: string): number {
  let hex = (hexColor || '').replace('#', '').trim();
  if (hex.length === 3) {
    hex = hex.split('').map(c => c + c).join('');
  }
  if (hex.length !== 6) return 128;

  const r = parseInt(hex.substring(0, 2), 16) || 0;
  const g = parseInt(hex.substring(2, 4), 16) || 0;
  const b = parseInt(hex.substring(4, 6), 16) || 0;

  return (r * 299 + g * 587 + b * 114) / 1000;
}

/**
 * Returns accessible text color (#09090B or #FFFFFF) for a given background hex.
 */
export function getAccessibleTextColor(backgroundHex: string): string {
  const lum = getLuminance(backgroundHex);
  return lum >= 155 ? '#09090B' : '#FFFFFF';
}

/**
 * Builds the clean white theme tokens for the public booking portal.
 * All public pages are pure white (#FFFFFF), with brand colors used strictly as accents.
 */
export function getThemeTokens(
  _portalTheme?: string | null,
  brandColorProp?: string | null
): ThemeTokens {
  let brandColor = (brandColorProp || '').trim();
  if (!brandColor || brandColor === '') {
    brandColor = '#18181B';
  }

  const brandLum = getLuminance(brandColor);

  // If brand color is pure white or near-white, buttons and links fallback to clean black
  const effectiveBrandBg = brandLum > 230 ? '#18181B' : brandColor;
  const effectiveBrandText = getAccessibleTextColor(effectiveBrandBg);

  return {
    isDark: false,
    bgPage: '#FFFFFF',
    bgSurface: '#FFFFFF',
    bgSubtle: '#F9FAFB',
    bgOverlay: 'rgba(255, 255, 255, 0.95)',
    borderSubtle: '#E5E7EB',
    borderHover: '#D1D5DB',
    textPrimary: '#111827',
    textSecondary: '#4B5563',
    textMuted: '#9CA3AF',
    brandColor: effectiveBrandBg,
    brandButtonBg: effectiveBrandBg,
    brandButtonText: effectiveBrandText,
    brandBadgeBg: `${effectiveBrandBg}14`,
    brandBadgeText: effectiveBrandBg,
    pillActiveBg: effectiveBrandBg,
    pillActiveText: effectiveBrandText,
    pillInactiveBg: '#FFFFFF',
    pillInactiveText: '#111827',
    pillInactiveBorder: '#D1D5DB',
    searchBg: '#FFFFFF',
    searchBorder: '#E5E7EB',
    searchText: '#111827',
    searchPlaceholder: '#9CA3AF',
    cardBg: '#FFFFFF',
    cardBorder: '#E5E7EB',
    cardHoverBorder: '#D1D5DB',
    accentLink: effectiveBrandBg,
    iconBg: '#F3F4F6',
    iconText: '#374151'
  };
}
